import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { OfflineReadinessStore } from '@animus/utility/web/service-worker';
import { injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogDescription, HlmDialogTitle } from '@spartan-ng/helm/dialog';

interface OfflineStatusDialogContext {
    close: () => void;
}

/** Explains whether the installed service worker can serve the current app shell when the network is unavailable. */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [HlmButtonImports, HlmDialogDescription, HlmDialogTitle],
    selector: 'app-offline-status-dialog',
    template: `
        <div class="min-w-0 pr-8">
            <h2 class="text-lg font-semibold" hlmDialogTitle>Offline availability</h2>
            <p class="mt-1 text-sm text-muted-foreground" hlmDialogDescription>{{ readiness.label() }}</p>
        </div>

        <div class="mt-4 text-sm text-muted-foreground">
            @switch (readiness.status()) {
                @case ('ready') {
                    <p>
                        The active version completed its update check. Offline availability depends on the browser
                        cache.
                    </p>
                }
                @case ('update-ready') {
                    <p>
                        An updated version is ready to activate the next time you reload. Your current game remains
                        open until then.
                    </p>
                }
                @case ('downloading') {
                    <p>An updated version is downloading. Keep this tab open and connected until it finishes.</p>
                }
                @case ('checking') {
                    <p>Checking the active version for updates.</p>
                }
                @case ('unavailable') {
                    <p>Offline caching is unavailable in this build or browser.</p>
                }
                @case ('error') {
                    <p role="alert">{{ readiness.error() ?? 'Offline content could not be cached.' }}</p>
                }
            }
        </div>

        <div class="mt-5 flex justify-end">
            <button (click)="context.close()" hlmBtn variant="outline" type="button">Close</button>
        </div>
    `,
})
export class OfflineStatusDialog {
    protected readonly context = injectBrnDialogContext<OfflineStatusDialogContext>();

    protected readonly readiness = inject(OfflineReadinessStore);
}
